import { useContext, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { cn } from "../lib/cn";
import { useUiStrings } from "../lib/strings";
import { FieldIdContext } from "./FormControls";
import { Icon } from "./Icon";
import { Popover, PopoverContent, PopoverTrigger } from "./Overlays";
import { Spinner } from "./Spinner";

export interface SelectOption {
  value: string;
  label: string;
  /** Second line under the label (e.g. a code or a parent category). Also searched. */
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Posts the value with a plain HTML form (hidden input). */
  name?: string;
  /**
   * Remote search for long lists: called (debounced) with the typed text; the parent loads the matching options
   * from the server and passes them in `options`. Without it the options are filtered here.
   */
  onSearch?: (text: string) => void;
  /** Label of the chosen value when it isn't among the loaded options (remote search). */
  selectedLabel?: string;
  /** The server is loading options for the current search. */
  loading?: boolean;
  /** Delay before `onSearch` runs, in ms. */
  debounce?: number;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
}

/** Lower-case and strip accents, so "Mahindra" matches "mahi" and "Pune" matches "PUNE". */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * Pick one value from a list, with a search box (every dropdown is searchable). Short and medium lists are filtered
 * in the browser; for long lists pass `onSearch` and load the matches from the server (remote search), so all rows
 * never load at once. Keyboard: Enter / Space / ↓ or typing opens it, ↑ ↓ Home End move, Enter picks, Esc closes.
 */
export function Select({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  id,
  name,
  onSearch,
  selectedLabel,
  loading = false,
  debounce = 250,
  ...aria
}: SelectProps) {
  const fieldId = useContext(FieldIdContext);
  const strings = useUiStrings();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef(onSearch);
  useEffect(() => {
    search.current = onSearch;
  }, [onSearch]);

  const remote = Boolean(onSearch);
  const chosen = options.find((option) => option.value === value);
  const shown = chosen?.label ?? (value ? selectedLabel : undefined);

  const visible = useMemo(() => {
    const needle = fold(text.trim());
    if (remote || !needle) return options;
    return options.filter((option) =>
      fold(`${option.label} ${option.description ?? ""}`).includes(needle),
    );
  }, [options, text, remote]);
  const current = Math.min(active, visible.length - 1);

  // Remote search: ask the parent for matches while open (also once on open, with "").
  useEffect(() => {
    if (!open || !remote) return;
    const timer = setTimeout(() => search.current?.(text.trim()), text ? debounce : 0);
    return () => clearTimeout(timer);
  }, [open, remote, text, debounce]);

  useEffect(() => {
    if (!open) return;
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${current}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [current, open]);

  // The chosen option is highlighted when the list opens, the first match while searching.
  const openList = (initial = "") => {
    setText(initial);
    setActive(
      initial
        ? 0
        : Math.max(
            options.findIndex((option) => option.value === value),
            0,
          ),
    );
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setText("");
  };

  const pick = (option: SelectOption | undefined) => {
    if (!option || option.disabled) return;
    if (option.value !== value) onValueChange?.(option.value);
    close();
  };

  const move = (step: number) => {
    if (!visible.length) return;
    let next = current;
    for (let i = 0; i < visible.length; i++) {
      next = (next + step + visible.length) % visible.length;
      if (!visible[next]?.disabled) break;
    }
    setActive(next);
  };

  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Home" && !text) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End" && !text) {
      event.preventDefault();
      setActive(visible.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      pick(visible[current]);
    } else if (event.key === "Tab") {
      close();
    }
  };

  // Typing on the closed trigger opens the list with that text, like a native select's type-ahead.
  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openList();
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (event.key === " ") return; // Space opens through the button's click
      event.preventDefault();
      openList(event.key);
    }
  };

  return (
    <Popover open={open} onOpenChange={(next) => (next ? openList() : close())}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id ?? fieldId}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-haspopup="listbox"
          aria-label={aria["aria-label"]}
          aria-invalid={aria["aria-invalid"]}
          aria-describedby={aria["aria-describedby"]}
          aria-required={aria["aria-required"]}
          disabled={disabled}
          data-placeholder={shown ? undefined : ""}
          onKeyDown={onTriggerKey}
          className={cn(
            "flex h-11 w-full items-center justify-between gap-2 rounded-input border border-input bg-surface px-3.5 " +
              "text-left text-sm text-foreground outline-none transition-colors data-[placeholder]:text-muted-foreground " +
              "focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-brand " +
              "disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-danger",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate">
            {shown ?? placeholder ?? strings.selectPlaceholder}
          </span>
          <Icon name="chevron-down" size={16} className="shrink-0" />
        </button>
      </PopoverTrigger>
      {name && <input type="hidden" name={name} value={value ?? ""} />}
      <PopoverContent
        className="flex max-h-80 w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[min(28rem,calc(100vw-1rem))] flex-col p-0"
        onOpenAutoFocus={(event) => {
          // Focus the search box (not the first option) so typing filters straight away.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector("input")?.focus();
        }}
      >
        <div className="relative border-b border-border p-1.5">
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-muted-foreground">
            <Icon name="search" size={14} />
          </span>
          <input
            type="search"
            aria-label={strings.search}
            aria-controls={listId}
            aria-activedescendant={visible[current] ? `${listId}-${current}` : undefined}
            autoComplete="off"
            placeholder={strings.search}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setActive(0);
            }}
            onKeyDown={onSearchKey}
            className="h-9 w-full rounded-lg bg-surface-muted pr-2 pl-8 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-brand"
          />
        </div>
        <ul
          ref={list}
          id={listId}
          role="listbox"
          aria-label={aria["aria-label"]}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
        >
          {loading && (
            <li
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
              aria-live="polite"
            >
              <Spinner size={14} label={strings.loading} />
            </li>
          )}
          {!loading && visible.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground" aria-live="polite">
              {strings.noResults}
            </li>
          )}
          {!loading &&
            visible.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                onMouseMove={() => current !== index && setActive(index)}
                className={cn(
                  "relative flex cursor-pointer items-start rounded-lg py-2 pr-8 pl-3 text-sm select-none",
                  index === current && "bg-brand-soft",
                  option.disabled && "cursor-not-allowed opacity-50",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </span>
                {option.value === value && (
                  <Icon name="check" size={16} className="absolute top-2.5 right-2" />
                )}
              </li>
            ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
